import React, { useRef, useState } from 'react'
import { Modal } from 'semantic-ui-react'
import Webcam from 'react-webcam'
import { InspectionService } from 'utility'
import { ActionPlanService } from 'utility/services/action-plan'
import { Btn } from './styles'
import { TicketFileTile } from 'components/TicketFileTile'
import { isImageAttachment } from 'utility/ticket-attachment'

const getPreviewUrl = file =>
    file?.FileURL ||
    file?.SignedURL ||
    file?.ImagePathUrl ||
    file?.url ||
    ''

/**
 * Upload a file (any type) / Take picture for AutoTicket comments.
 * Uses the same Inspection media upload stack as ActionPlan tickets.
 * Attach to ticket via Medias/Update when possible; parent posts comment text.
 */
export const TicketCommentMedia = ({
    ticketId,
    disabled,
    files,
    onChange,
}) => {
    const fileInputRef = useRef(null)
    const webcamRef = useRef(null)
    const [uploading, setUploading] = useState(false)
    const [cameraOpen, setCameraOpen] = useState(false)
    const [previewUrl, setPreviewUrl] = useState('')
    const [error, setError] = useState('')

    const mergeFiles = next => {
        onChange?.(next)
    }

    const uploadOne = async file => {
        const uploadResponse = await InspectionService.uploadMedia({
            media: file,
        })
        const fileUrl =
            uploadResponse?.ImagePathUrl ||
            uploadResponse?.FileURL ||
            uploadResponse?.SignedURL ||
            ''
        const fileKey =
            uploadResponse?.CloudImageId ||
            uploadResponse?.FileKey ||
            ''
        const saved = await InspectionService.saveMedia([
            {
                MediaId: 0,
                FAQId: Number(ticketId) || 0,
                Order: 1,
                FolderName: '1',
                FileType: file.type || 'application/octet-stream',
                FileName: (file.name || '').slice(0, 200),
                SignedURL: fileUrl,
                FileURL: fileUrl,
                FileKey: fileKey,
                MediaSourceType: 2,
            },
        ])
        const row = Array.isArray(saved) ? saved[0] : saved?.Data?.[0] || saved
        const mediaId =
            row?.MediaId ??
            row?.mediaId ??
            uploadResponse?.MediaId ??
            null
        return {
            MediaId: mediaId,
            FileURL: fileUrl,
            FileKey: fileKey,
            FileType: file.type || 'application/octet-stream',
            FileName: file.name || '',
        }
    }

    const attachToTicket = async mediaIds => {
        const ids = (mediaIds || []).filter(Boolean)
        if (!ids.length || !ticketId) return
        try {
            await ActionPlanService.updateMediaReferences(ids, ticketId)
        } catch {
            // AutoTicket may not share ActionPlan media refs yet — previews still work locally.
        }
    }

    const handleFiles = async fileList => {
        // Any file type (2026-10-28); pictures keep their thumbnail, other files show as a file tile.
        const list = Array.from(fileList || [])
        if (!list.length) return
        setError('')
        setUploading(true)
        try {
            const uploaded = await Promise.all(list.map(file => uploadOne(file)))
            await attachToTicket(uploaded.map(u => u.MediaId))
            mergeFiles([...(files || []), ...uploaded])
        } catch (err) {
            setError(
                err?.message ||
                    'Could not upload the file. Try again or ask admin to enable ticket media.',
            )
        } finally {
            setUploading(false)
            if (fileInputRef.current) fileInputRef.current.value = ''
        }
    }

    const capturePhoto = async () => {
        const dataUrl = webcamRef.current?.getScreenshot()
        if (!dataUrl) return
        setCameraOpen(false)
        const res = await fetch(dataUrl)
        const blob = await res.blob()
        const file = new File([blob], `ticket-${Date.now()}.jpg`, {
            type: 'image/jpeg',
        })
        await handleFiles([file])
    }

    const removeAt = index => {
        mergeFiles((files || []).filter((_, i) => i !== index))
    }

    return (
        <div style={{ marginTop: 10 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Btn
                    type="button"
                    disabled={disabled || uploading}
                    onClick={() => fileInputRef.current?.click()}
                >
                    {uploading ? 'Uploading…' : '⬆ Upload file'}
                </Btn>
                <Btn
                    type="button"
                    disabled={disabled || uploading}
                    onClick={() => setCameraOpen(true)}
                >
                    📷 Take picture
                </Btn>
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    style={{ display: 'none' }}
                    onChange={e => handleFiles(e.target.files)}
                />
            </div>
            {error && (
                <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 6 }}>
                    {error}
                </div>
            )}
            {!!(files || []).length && (
                <div
                    style={{
                        display: 'flex',
                        gap: 8,
                        flexWrap: 'wrap',
                        marginTop: 10,
                    }}
                >
                    {(files || []).map((file, index) => {
                        const src = getPreviewUrl(file)
                        if (src && !isImageAttachment(file)) {
                            return (
                                <TicketFileTile
                                    key={file.MediaId || src || index}
                                    url={src}
                                    file={file}
                                    onRemove={() => removeAt(index)}
                                />
                            )
                        }
                        return (
                            <button
                                key={file.MediaId || src || index}
                                type="button"
                                onClick={() => setPreviewUrl(src)}
                                style={{
                                    position: 'relative',
                                    width: 64,
                                    height: 64,
                                    padding: 0,
                                    border: '1px solid #d1d5db',
                                    borderRadius: 8,
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    background: '#f8fafc',
                                }}
                                title="Preview image"
                            >
                                {src ? (
                                    <img
                                        src={src}
                                        alt={`Attachment ${index + 1}`}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                        }}
                                    />
                                ) : (
                                    <span style={{ fontSize: 20 }}>🖼</span>
                                )}
                                <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={e => {
                                        e.stopPropagation()
                                        removeAt(index)
                                    }}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                            e.stopPropagation()
                                            removeAt(index)
                                        }
                                    }}
                                    style={{
                                        position: 'absolute',
                                        top: 2,
                                        right: 2,
                                        width: 18,
                                        height: 18,
                                        borderRadius: '50%',
                                        background: 'rgba(15,23,42,0.75)',
                                        color: '#fff',
                                        fontSize: 11,
                                        lineHeight: '18px',
                                        textAlign: 'center',
                                    }}
                                >
                                    ×
                                </span>
                            </button>
                        )
                    })}
                </div>
            )}

            <Modal
                open={cameraOpen}
                onClose={() => setCameraOpen(false)}
                size="small"
            >
                <Modal.Header>Take picture</Modal.Header>
                <Modal.Content>
                    <Webcam
                        ref={webcamRef}
                        audio={false}
                        screenshotFormat="image/jpeg"
                        videoConstraints={{ facingMode: 'environment' }}
                        style={{ width: '100%', borderRadius: 8 }}
                    />
                </Modal.Content>
                <Modal.Actions>
                    <Btn type="button" onClick={() => setCameraOpen(false)}>
                        Cancel
                    </Btn>
                    <Btn type="button" $primary onClick={capturePhoto}>
                        Capture
                    </Btn>
                </Modal.Actions>
            </Modal>

            <Modal
                open={Boolean(previewUrl)}
                onClose={() => setPreviewUrl('')}
                size="small"
            >
                <Modal.Header>Image preview</Modal.Header>
                <Modal.Content>
                    {previewUrl && (
                        <img
                            src={previewUrl}
                            alt="Preview"
                            style={{
                                width: '100%',
                                borderRadius: 8,
                                display: 'block',
                            }}
                        />
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Btn type="button" onClick={() => setPreviewUrl('')}>
                        Close
                    </Btn>
                </Modal.Actions>
            </Modal>
        </div>
    )
}

export const HistoryMediaThumbs = ({ urls, onOpen }) => {
    const list = (urls || []).filter(Boolean)
    if (!list.length) return null
    return (
        <div
            style={{
                display: 'flex',
                gap: 6,
                flexWrap: 'wrap',
                marginTop: 6,
            }}
        >
            {list.map((url, i) =>
                !isImageAttachment({ url }) ? (
                    <TicketFileTile key={`${url}-${i}`} url={url} size={44} />
                ) : (
                <button
                    key={`${url}-${i}`}
                    type="button"
                    onClick={() => onOpen?.(url)}
                    style={{
                        width: 44,
                        height: 44,
                        padding: 0,
                        border: '1px solid #d1d5db',
                        borderRadius: 6,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        background: '#f8fafc',
                    }}
                    title="View image"
                >
                    <img
                        src={url}
                        alt=""
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                        }}
                    />
                </button>
                ),
            )}
        </div>
    )
}
